import { Injectable } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of, throwError, EMPTY } from 'rxjs';
import { EnvelopeService } from '../../../envelope/services/envelope.service';
import { map, catchError, switchMap } from 'rxjs/operators';
import { SignatureRoutePages } from '../enum/signature-route-pages';
import { AccessTokenService } from './access-token.service';
import { RoutePages } from '../../../enum/route-pages';
import { AuthService } from '../../../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageBase } from 'src/app/envelope/model/language';

const URL_SIGNATURE = RoutePages.Signature;
const URL_SIGNTUREACCESSTOKEN = RoutePages.Signature
  .concat('/')
  .concat(SignatureRoutePages.AccessToken);
const URL_SIGNATUREENVELOPE = RoutePages.Signature
  .concat('/')
  .concat(SignatureRoutePages.Envelope);

@Injectable({
  providedIn: 'root'
})
export class InitialValidationResolverService implements Resolve<any> {

  languageBase: LanguageBase;
  languageValue: string;
  private accessKey = 'accessKey';
  private savedAccessKeyFromIgnoredBatchSign = 'savedAccessKeyFromIgnoredBatchSign';
  private batch = 'batch';
  private signatureType = 'signatureType';

  constructor(
    private translateService: TranslateService,
    private envelopeService: EnvelopeService,
    private router: Router,
    private accessTokenService: AccessTokenService,
    private authService: AuthService) {
    this.languageBase = new LanguageBase(translateService);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Observable<never> {
    let completed;
    switch (route.routeConfig.path as SignatureRoutePages) {
      case SignatureRoutePages.AccessToken:
        completed = this.accessKeyValidation();
        return completed;
      case SignatureRoutePages.Envelope:
        completed = this.accessTokenValidation();
        return completed;
      default:
        completed = this.initialValidation(route);
        return completed;
    }
  }

  accessKeyValidation() {
    if (!this.accessTokenService.getAccessKey() && !this.accessTokenService.restore()) {
      this.router.navigate([URL_SIGNATURE]);
    }
    return of({});
  }

  private accessTokenValidation() {
    return this.accessKeyValidation().pipe(
      switchMap(() => {
        if (!this.accessTokenService.getAccessKey() && !this.authService.authModel) {
          this.router.navigate([URL_SIGNATURE]);
        }

        // After a reload the restored accessKey has not been validated yet.
        if (this.accessTokenService.getAccessKey() && !this.authService.signUserModel) {
          return this.validateByAccessKey(
            this.accessTokenService.getAccessKey(),
            this.accessTokenService.getAccessToken(),
            this.accessTokenService.getBatch()
          ).pipe(map(() => ({})));
        }

        return of({});
      })
    );
  }

  private validateAccessKey(accessKey: string) {
    return this.envelopeService.initialValidation(accessKey)
      .pipe(
        map(() => {
          this.accessTokenService.setAccessKey(accessKey);
          return EMPTY;
        }),
        catchError(data => throwError(data.error))
      );
  }

  private validateByAccessKey(accessKey: string, accessToken: string, batch: boolean) {
    return this.authService.getValidateByAccessKey(accessKey, accessToken, batch)
      .pipe(
        map((data) => {
          this.languageValue = this.languageBase.getValue(data.signUser.language);
          this.translateService.use(this.languageValue);
          this.accessTokenService.setAccessKey(accessKey);
          return EMPTY;
        }),
        catchError(data => throwError(data.error))
      );
  }

  private initialValidation(route: ActivatedRouteSnapshot) {
    if (route.queryParams) {
      var accessKey = route.queryParams[this.accessKey] ? route.queryParams[this.accessKey] : route.queryParams[this.savedAccessKeyFromIgnoredBatchSign] ? route.queryParams[this.savedAccessKeyFromIgnoredBatchSign] : undefined;

      if (accessKey) {
        this.accessTokenService.setAccessKey(accessKey);

        const batch = route.queryParams[this.batch] ? true : false;
        this.accessTokenService.setBatch(batch);

        const signatureType = route.queryParams[this.signatureType] ? route.queryParams[this.signatureType] : null;
        this.accessTokenService.setSignatureType(signatureType);

        return this.validateByAccessKey(accessKey, '', batch)
          .pipe(
            map(() => {
              if (route.queryParams[this.accessKey])
                this.router.navigate([URL_SIGNATUREENVELOPE]);
            }),
            catchError(data => throwError(data.error))
          );

      } else {
        return of({ exception: 'invalidParam', language: '' });
      }
    } else {
      return of({ exception: 'invalidParam', language: '' });
    }
  }
}